// agentWallet.ts — Phase 3 agent-side wallet store.
//
// Mirrors the player's lib/wallet.ts pattern: tiny event-bus module
// so the chrome chip, the wallet panel, and any dashboard tile all
// stay in sync without prop-drilling a context through the tree.
//
// Backend source of truth: GET /api/admin/me-agent/wallet
// (auth required + 'earnings' view permission). The endpoint returns
// `{ balance, rows, total, limit, offset }`. We only consume `balance`
// here — the ledger rows live on the wallet panel which fetches its
// own page.
//
// 403 path: agents whose role permissions don't include `earnings:view`
// see 403 on this endpoint. The chip falls back to `available=false`
// in that case so AgentNavBar can hide the chip rather than crash.
//
// Units: balance_points (signed bigint). Agent's pool can go negative
// under settlement debit — chip will render the leading minus sign.

import { useEffect, useState } from 'react';
import { adminApi } from './adminApi';
import { ApiError } from '../../lib/api';

export type AgentWalletState = {
  balance: number;
  loading: boolean;
  /** false when the API returned 403 (agent lacks `earnings:view`). */
  available: boolean;
  error?: string;
};

type Listener = (s: AgentWalletState) => void;
const listeners = new Set<Listener>();
let state: AgentWalletState = { balance: 0, loading: true, available: true };

function emit() { for (const l of listeners) l(state); }

export async function refreshAgentWallet(): Promise<void> {
  state = { ...state, loading: true, error: undefined };
  emit();
  try {
    const data = await adminApi.get<{ balance: number }>('/admin/me-agent/wallet?limit=1&offset=0');
    state = { balance: Number(data.balance) | 0, loading: false, available: true };
  } catch (e) {
    const err = e as ApiError;
    // 403 = no `earnings:view` permission. Chrome chip hides itself.
    if (err.status === 403) {
      state = { balance: 0, loading: false, available: false };
    } else {
      state = { ...state, loading: false, error: err.message };
    }
  }
  emit();
}

/** Imperative setter — used by approvals / settlement push to avoid
 *  a round-trip to /me-agent/wallet. */
export function setAgentWalletBalance(next: number) {
  state = { ...state, balance: next | 0, loading: false, available: true };
  emit();
}

export function useAgentWallet(): AgentWalletState {
  const [s, setS] = useState<AgentWalletState>(state);
  useEffect(() => {
    listeners.add(setS);
    // Always refresh on first mount so navigating into the agent
    // shell after any wallet-mutating action picks up the new value.
    refreshAgentWallet();
    // Refresh whenever the agent tab regains focus — covers the
    // common pattern of switching to UPI / Telegram / Mail to confirm
    // a recharge, then tabbing back. Cheap (one tiny endpoint), no
    // polling overhead.
    const onFocus = () => { refreshAgentWallet(); };
    window.addEventListener('focus', onFocus);
    return () => {
      listeners.delete(setS);
      window.removeEventListener('focus', onFocus);
    };
  }, []);
  return s;
}
